import React, { useState, useEffect } from 'react';
import { useStudent } from '../context/StudentContext';
import { User, Phone, Mail, MapPin, Save, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Profile.css';

const Profile = () => {
    const { student, refetch } = useStudent();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email_address: '',
        phone_number: '',
        whatsapp_number: '',
        gender: '',
        national_id__no: '',
        date_of_birth: '',
        relationship_with_next_of_kin: '',
        next_of_kin_phone_number: ''
    });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (student) {
            fetchStudentDetails();
        }
    }, [student]);

    const fetchStudentDetails = async () => {
        const nonce = window.rpVars?.nonce;
        const res = await fetch(`/wp-json/sdt/v1/customer/${student.id}`, {
            headers: { 'X-WP-Nonce': nonce }
        });
        if (res.ok) {
            const data = await res.json();
            setFormData({
                first_name: data.first_name || '',
                last_name: data.last_name || '',
                email_address: data.email_address || '',
                phone_number: data.phone_number || '',
                whatsapp_number: data.whatsapp_number || '',
                gender: data.gender || '',
                national_id__no: data.national_id__no || '',
                date_of_birth: data.date_of_birth || '',
                relationship_with_next_of_kin: data.relationship_with_next_of_kin || '',
                next_of_kin_phone_number: data.next_of_kin_phone_number || ''
            });
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');

        const nonce = window.rpVars?.nonce;
        try {
            const res = await fetch(`/wp-json/sdt/v1/customer/${student.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setMessage('Profile updated successfully!');
                refetch();
            } else {
                setMessage('Failed to update profile.');
            }
        } catch (error) {
            setMessage('Error saving profile.');
        } finally {
            setSaving(false);
        }
    };

    if (!student) return null;

    return (
        <div className="page-container profile-page">
            <header className="profile-header flex items-center justify-between">
                <div>
                    <Link to="/" className="text-indigo-400 flex items-center gap-2 mb-2 text-sm font-bold uppercase tracking-wider">
                        <ArrowLeft size={16} /> Back to Dashboard
                    </Link>
                    <h1 className="text-3xl font-bold font-display">My Profile</h1>
                </div>
            </header>

            <div className="profile-grid">
                <div className="card profile-info-card">
                    <div className="avatar-large">{student.avatar}</div>
                    <h2 className="profile-name">{student.name}</h2>
                    <p className="profile-id">Student ID: {student.studentId}</p>
                    <div className="bg-slate-700/30 p-4 rounded-2xl text-left">
                        <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">Registration Status</p>
                        <div className="flex items-center gap-2 text-green-400">
                            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                            <span className="font-bold text-sm">Active Enrolment</span>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <form onSubmit={handleSubmit} className="form-grid">
                        <div className="form-section-title">Personal Details</div>
                        <div className="input-group">
                            <label>First Name</label>
                            <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>Last Name</label>
                            <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>Email Address</label>
                            <input type="email" name="email_address" value={formData.email_address} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>Phone Number</label>
                            <input type="text" name="phone_number" value={formData.phone_number} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>WhatsApp Number</label>
                            <input type="text" name="whatsapp_number" value={formData.whatsapp_number} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>National ID No</label>
                            <input type="text" name="national_id__no" value={formData.national_id__no} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>Date of Birth</label>
                            <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>Gender</label>
                            <select name="gender" value={formData.gender} onChange={handleChange}>
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div className="form-section-title mt-6">Emergency Contact</div>
                        <div className="input-group">
                            <label>Relationship with Next of Kin</label>
                            <input type="text" name="relationship_with_next_of_kin" value={formData.relationship_with_next_of_kin} onChange={handleChange} placeholder="e.g. Parent, Sibling" />
                        </div>
                        <div className="input-group">
                            <label>Next of Kin Phone</label>
                            <input type="text" name="next_of_kin_phone_number" value={formData.next_of_kin_phone_number} onChange={handleChange} />
                        </div>

                        <div className="full-width mt-4 flex items-center justify-between">
                            {message && (
                                <p className={`text-sm font-bold ${message.includes('Success') ? 'text-green-400' : 'text-red-400'}`}>
                                    {message}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn btn-primary ml-auto flex items-center gap-2 px-8 py-3 rounded-xl"
                            >
                                <Save size={18} />
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Profile;
